import generatePalette from '~utils/generate-palette';
import {
    APP_DONUT_COLORS,
    DEVICE_DONUT_COLORS,
    GRAY_100,
    PURPLE_DONUT_RAMP,
    RED_600,
    RED_200,
} from '~styles/colors';
import { resolveLabelKeys } from './chart-keys';
import { resolveSeriesColors } from './palette-colors';
import type { KpiPalette } from '../types';

/** Label of the residual slice, rendered gray (see onboarding pie convention). */
const OTHER_SLICE_LABEL = 'other';

/** `campaign` palette: CT red first, pale pink second, by position (not by
 * label, unlike `device`/`app`) since every Contactability donut names its
 * two slices differently. */
const CAMPAIGN_DONUT_COLORS_BY_POSITION = [RED_600, RED_200];

export interface PieSlice {
    label: string;
    value: number;
}

const isOtherSlice = ({ label }: PieSlice): boolean =>
    label.toLowerCase() === OTHER_SLICE_LABEL;

const resolveRampColors = (
    slices: PieSlice[],
    paletteName?: KpiPalette
): string[] => {
    if (paletteName === 'campaign') {
        return generatePalette(
            CAMPAIGN_DONUT_COLORS_BY_POSITION,
            slices.length
        );
    }
    const themedColors = resolveSeriesColors(
        slices
            .filter((slice) => !isOtherSlice(slice))
            .map(({ label }) => label),
        paletteName,
        'fill'
    );
    if (!themedColors) {
        return generatePalette(PURPLE_DONUT_RAMP, slices.length);
    }
    let nextThemedColor = 0;
    return slices.map((slice) =>
        isOtherSlice(slice) ? GRAY_100 : themedColors[nextThemedColor++]
    );
};

/** See `resolveLabelKeys`: stable per-slice keys, shared with the other
 * chart forms. */
export const resolvePieSliceKeys = (slices: PieSlice[]): string[] =>
    resolveLabelKeys(slices);

export interface ResolvedPieSlice extends PieSlice {
    /** Share of the total, rounded to a whole percent (0 when the total is 0). */
    pct: number;
    /** Fill color: a purple ramp entry, or gray for an "Other" slice. */
    color: string;
}

/**
 * Turn raw pie slices into render-ready slices: a whole-percent share of the
 * total plus a fill color. A named brand palette (`paletteName` 'device'/'app')
 * maps color by slice label (case-insensitive), so iOS/Android and
 * Couche-Tard/Circle K keep their brand color no matter the envelope's row order
 * or label casing, and an unlisted label falls back to the positional purple
 * ramp; `campaign` maps by position instead (CT red, then pale pink), since the
 * Contactability donuts name their two slices differently card to card;
 * a theme palette reads `resolveSeriesColors` over the slices that are not
 * "Other". Without a named palette every slice takes the purple ramp by
 * position, extended by `generatePalette` when there are more slices than ramp
 * entries. Any slice labeled "Other" (case-insensitive) is grayed, matching the
 * segmentation pie.
 */
export const resolvePieSlices = (
    slices: PieSlice[],
    paletteName?: KpiPalette
): ResolvedPieSlice[] => {
    const total = slices.reduce((sum, slice) => sum + slice.value, 0);
    const colorByLabel =
        paletteName === 'device'
            ? DEVICE_DONUT_COLORS
            : paletteName === 'app'
            ? APP_DONUT_COLORS
            : undefined;
    // Lower-cased lookup so envelope casing drift (e.g. "ios") still maps to the
    // brand color instead of silently falling back to the ramp.
    const colorByLowerLabel = colorByLabel
        ? new Map(
              Object.entries(colorByLabel).map(([label, color]) => [
                  label.toLowerCase(),
                  color,
              ])
          )
        : undefined;
    const rampPalette = resolveRampColors(slices, paletteName);

    return slices.map((slice, index) => ({
        label: slice.label,
        value: slice.value,
        pct: total > 0 ? Math.round((slice.value / total) * 100) : 0,
        // A named palette maps by label (brand color follows the slice, not its
        // position); otherwise fall back to the positional ramp. A zero total
        // means there is nothing to plot, so every slice grays out instead.
        color:
            total <= 0 || isOtherSlice(slice)
                ? GRAY_100
                : colorByLowerLabel?.get(slice.label.toLowerCase()) ??
                  rampPalette[index],
    }));
};
